import React, { useContext, useEffect, useRef, useState } from 'react';
import t from 'react-translate';
import { css } from '@emotion/core';

// redux
import { QuizQuestionType } from 'redux/schemas/models/quiz';
import { useSelector, useDispatch } from 'react-redux';
import { getFlatCourseAliases } from 'redux/selectors/course';
import { editQuizQuestionOption } from 'redux/actions/quizzes';
import { wrapThunkAction } from 'redux/utils';

import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvTooltip from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import { quarterSpacing, threeQuartersSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';
import { gray5, primary } from 'styles/global_defaults/colors';
import { removeDecimalForInteger } from 'quizzes/components/utils';
import AnswerFeedback from 'quizzes/components/feedback-components/answer-feedback';
import QuizContext, { QuestionContext, SavingIndicator } from '../../context';
import NumberAnswerSettingContainer from '../number-answer-question/number-answer-setting-container';

type TabelCellPopoverContentProps = {
  shortAnswer: string;
  setShortAnswer: Function;
  isAnswerInRange: boolean;
  setIsAnswerInRange: Function;
};

const TabelCellPopoverContent = (props: TabelCellPopoverContentProps) => {
  const {
    shortAnswer,
    setShortAnswer,
    isAnswerInRange,
    setIsAnswerInRange,
  } = props;

  const {
    currentQuestion,
    responseOptions,
  } = useContext(QuestionContext);

  const {
    savingIndicatorTimeoutRef,
    setSavingStatus,
  } = useContext(QuizContext);

  const questionType = currentQuestion.type;
  const isTopRangePresent = !!responseOptions[0]?.topOfRange
    && (responseOptions[0].topOfRange !== responseOptions[0]?.bottomOfRange);
  const dispatch = useDispatch();

  const popoverContentRef = useRef(null);

  const [triggerShowFeedback, setTriggerShowFeedback] = useState(false);
  const [correctAnswerFeedback, setCorrectAnswerFeedback] = useState(responseOptions[0]?.explanation);
  const [incorrectAnswerFeedback, setIncorrectAnswerFeedback] = useState(responseOptions[0]?.secondaryExplanation);
  const [bottomOfRange, setBottomOfRange] = useState(removeDecimalForInteger(responseOptions[0]?.bottomOfRange) || '');
  const [topOfRange, setTopOfRange] = useState(isTopRangePresent ? removeDecimalForInteger(responseOptions[0]?.topOfRange) : '');
  const [feedbackContainerMaxWidth, setFeedbackContainerMaxWidth] = useState();

  useEffect(() => {
    if (popoverContentRef.current) {
      setFeedbackContainerMaxWidth(popoverContentRef.current.getBoundingClientRect().width);
    }
  }, [popoverContentRef, setFeedbackContainerMaxWidth, isAnswerInRange]);

  const popoverContentStyles = css`
    max-width: none !important;
    .short-answer-input-container {
      width: 100%;
      height: ${tripleSpacing}px;
      border-radius: ${quarterSpacing}px;
      border: 1px solid ${gray5};
      input {
        height: ${tripleSpacing}px;
        border: none;
        outline: none;
        background-color: transparent;
        font-size: 14px;
      }
      .feedback-icon-container {
        opacity: 0;
      }
      &:focus-within {
        border-color: ${primary};
      }
      &:focus-within, &:hover {
        .feedback-icon-container {
          opacity: 1 !important;
        }
      }
    }
    .number-answer-section {
      width: 100%;
      .dash-component {
        margin: 0px ${threeQuartersSpacing}px 0px ${threeQuartersSpacing}px;
      }
      .number-input-container {
        ${!isAnswerInRange && css`
          width: 100% !important;
        `}
      }
    }
    .feedback-setting-container {
      max-width: ${feedbackContainerMaxWidth}px !important;
    }
  `;

  useEffect(() => {
    if (isTopRangePresent) {
      setIsAnswerInRange(true);
    }
  }, [isTopRangePresent, setIsAnswerInRange]);

  const courseAliases = useSelector(getFlatCourseAliases);

  const handleShowFeedBackInput = (e) => {
    e.stopPropagation();
    setTriggerShowFeedback(true);
  };

  const handleShortAnswerChange = (e) => {
    setShortAnswer(e.target.value);
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);

    wrapThunkAction(dispatch(editQuizQuestionOption(
      { id: responseOptions[0].id,
        patch: { isCorrect: true, optionContent: e.target.value },
      },
    ))).then(() => {
      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
    }).catch(() => {
      setSavingStatus(SavingIndicator.ERROR_STATUS);
    }).finally(() => {
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  };

  const getAnswerSettingInput = () => {
    switch (questionType) {
      case QuizQuestionType.NUMBER_QUESTION:
        return (
          <div ref={popoverContentRef} className='d-flex flex-column align-items-center'>
            <p className='bold text-large-body mb-0'>{t.QUIZZES.ADD_CORRECT_NUMBER()}</p>
            <p className='mb-0 mt-4 text-medium'>{t.QUIZZES.TABLE_QUESTION.ADD_CORRECT_NUMBER_DESCRIPTION()}</p>
            <div className='number-answer-section mt-2'>
              <NumberAnswerSettingContainer
                setTriggerShowFeedback={setTriggerShowFeedback}
                isAnswerInRange={isAnswerInRange}
                setIsAnswerInRange={setIsAnswerInRange}
                topOfRange={topOfRange}
                bottomOfRange={bottomOfRange}
                setBottomOfRange={setBottomOfRange}
                setTopOfRange={setTopOfRange}
                showFeedbackIcon={correctAnswerFeedback === null || incorrectAnswerFeedback === null}
              />
            </div>
          </div>
        );
      case QuizQuestionType.ALL_QUESTION_VIEW_SHORT_TEXT_QUESTION:
        return (
          <div ref={popoverContentRef} className='d-flex flex-column align-items-center'>
            <p className='bold text-large-body mb-0'>{t.QUIZZES.ADD_CORRECT_ANSWER()}</p>
            <p className='mb-0 mt-4 text-medium'>{t.QUIZZES.TABLE_QUESTION.ADD_CORRECT_ANSWER_DESCRIPTION()}</p>
            <div
              className='d-flex mt-4 short-answer-input-container'
            >
              <NvTextInput
                value={shortAnswer}
                onChange={handleShortAnswerChange}
                maxLength={255}
                className='w-75'
                placeholder={t.FORM.PLACEHOLDER.INPUT_THE_ANSWER()}
              />
              <div className='feedback-icon-container d-flex justify-content-end w-25'>
                <NvTooltip text={t.QUIZZES.FEEDBACK_DEFAULT_INFO(courseAliases)}>
                  <NvIcon
                    onClick={handleShowFeedBackInput}
                    icon='comments'
                    size='small'
                    className='d-flex align-items-center text-primary mr-4'
                  />
                </NvTooltip>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div css={popoverContentStyles} className='p-1 d-flex flex-column align-items-center'>
      {getAnswerSettingInput()}
      <AnswerFeedback
        currentQuestion={currentQuestion}
        correctAnswerFeedback={correctAnswerFeedback}
        incorrectAnswerFeedback={incorrectAnswerFeedback}
        setCorrectAnswerFeedback={setCorrectAnswerFeedback}
        setIncorrectAnswerFeedback={setIncorrectAnswerFeedback}
        triggerShowFeedback={triggerShowFeedback}
        setTriggerShowFeedback={setTriggerShowFeedback}
      />
    </div>
  );
};

export default TabelCellPopoverContent;
